import type { ShopifyImage } from './types';

/**
 * Curated order of the product page gallery. Shopify product images are matched by a fragment of
 * their file name, so price/stock stay live while the order is editorial. Two editorial photos
 * from the landing fill gaps Shopify doesn't have yet (clean product shot, lifestyle).
 * Any Shopify image not listed here (e.g. uploaded later) is still shown, before the lifestyle shot.
 */

export type GalleryImage = ShopifyImage & {
  source: 'shopify' | 'editorial';
  /** i18n key for the alt text of editorial images */
  altKey?: string;
};

/** Clean product shot used wherever the product is shown small (cart lines, empty cart). */
export const PRODUCT_CLEAN_IMAGE = {
  url: '/assets/images/59ea6233-259b-431a-99e0-dba62b2624f0-1790626760819.webp',
  width: 1122,
  height: 1402,
};

type Slot =
  | { shopify: string }
  | { editorial: { url: string; width: number; height: number; altKey: string } };

const GALLERY: Slot[] = [
  // 1. Clean, premium product shot
  {
    editorial: {
      url: '/assets/images/59ea6233-259b-431a-99e0-dba62b2624f0-1790626760819.webp',
      width: 1122,
      height: 1402,
      altKey: 'product.imageAlt',
    },
  },
  // 2. Formats / adapters — answers the main purchase question before promotional graphics
  {
    editorial: {
      url: '/assets/images/c7aa0fdf-013c-4470-88ae-053ea81a8f26-1790593081273.webp',
      width: 1393,
      height: 1129,
      altKey: 'threeInOne.imageAlt',
    },
  },
  // 4. Heating
  { shopify: 'f0eb2082' },
  // 4b. Direct / thermal extraction
  { shopify: 'ddfe3a50' },
  // 5. Compatibility
  { shopify: 'd6d42d0b' },
  // 6. "Cafetera de cápsula portátil 3 en 1" infographic
  { shopify: '82131ace' },
  // 7. Lifestyle
  {
    editorial: {
      url: '/assets/images/75e9967e-76d5-48dd-a2f2-6519a72a6826-1790627256982.webp',
      width: 1122,
      height: 1402,
      altKey: 'cta.imageAlt',
    },
  },
];

export function buildGallery(shopifyImages: ShopifyImage[]): GalleryImage[] {
  const used = new Set<string>();
  const items: GalleryImage[] = [];

  for (const slot of GALLERY) {
    if ('editorial' in slot) {
      const { url, width, height, altKey } = slot.editorial;
      items.push({ url, width, height, altText: null, altKey, source: 'editorial' });
    } else {
      const image = shopifyImages.find((img) => img.url.includes(slot.shopify));
      if (image) {
        used.add(image.url);
        items.push({ ...image, source: 'shopify' });
      }
    }
  }

  const extra = shopifyImages
    .filter((img) => !used.has(img.url))
    .map((img): GalleryImage => ({ ...img, source: 'shopify' }));
  items.splice(items.length - 1, 0, ...extra);
  return items;
}
